<template>
  <section class="product-gift" aria-labelledby="product-gift-title">
    <h2 id="product-gift-title">Make it a gift</h2>
    <p>
      Thoughtfully wrapped, ready to give. Check delivery dates and returns
      before choosing your gift.
    </p>
    <div class="product-gift__links">
      <router-link to="/delivery">{{
        deliveryRange
          ? `Estimated arrival: ${deliveryRange}`
          : "Delivery information"
      }}</router-link>
      <router-link to="/refund_policy">{{
        finalSaleStatus === "TRUE"
          ? "Final sale — check return eligibility"
          : "Return policy"
      }}</router-link>
    </div>
    <label class="product-gift__choice">
      <input
        type="checkbox"
        :checked="busy || isGiftOrder"
        :disabled="busy || !product?.stock || !!product?.__productPreview"
        @change="chooseGift"
      />
      <span>Add gift wrapping to my order</span>
    </label>
    <p class="product-gift__hint">
      Selecting this adds this pair to your bag if needed and opens your gift
      order.
    </p>
    <p v-if="busy" role="status">Preparing your shopping bag…</p>
    <p v-if="error" class="product-gift__error" role="alert">{{ error }}</p>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useStore } from "vuex";
import { useGiftOrderPreference } from "../../../../../utils/giftOrder";
import {
  confirmedAddedQuantity,
  snapshotCartAnalyticsProduct,
} from "../../../../../utils/cartAnalytics";
import { useProductAnalytics } from "../composables/useProductAnalytics";

const props = defineProps({
  product: { type: Object, default: () => null },
  finalSaleStatus: { type: String, default: "UNKNOWN" },
  deliveryRange: { type: String, default: "" },
});
const emit = defineEmits(["added"]);
const store = useStore();
const { isGiftOrder, setIsGiftOrder } = useGiftOrderPreference();
const { trackAddToCart } = useProductAnalytics();
const busy = ref(false);
const error = ref("");

async function chooseGift(event: Event) {
  const checkbox = event.target as HTMLInputElement;
  const selected = checkbox.checked;
  if (!selected) {
    setIsGiftOrder(false);
    return;
  }
  if (busy.value || !props.product?.stock || props.product?.__productPreview) {
    checkbox.checked = isGiftOrder.value;
    return;
  }
  const product = props.product;
  const analyticsProduct = snapshotCartAnalyticsProduct(product);
  busy.value = true;
  error.value = "";
  try {
    // Refresh first so an existing server-side line is never added twice.
    const loaded = await store.dispatch("store/cart/load");
    if (!loaded?.ok) throw new Error("cart_unavailable");
    const inBag = loaded.cart?.products?.some(
      (line: any) => String(line.product?.id) === String(product.id)
    );
    if (!inBag) {
      const quantity = 1;
      const result = await store.dispatch("store/cart/add", {
        product,
        quantity,
      });
      if (!result?.ok) throw new Error("add_failed");
      // Tracked here, not by the "added" listener: an emit from a component
      // that unmounted during the request (SPA navigation) is dropped.
      const added = analyticsProduct
        ? confirmedAddedQuantity(result, analyticsProduct.id, quantity)
        : 0;
      try {
        if (analyticsProduct && added > 0)
          trackAddToCart(analyticsProduct, added);
      } catch {
        // Tracking must never turn a completed gift order into an error.
      }
      emit("added");
    }
    setIsGiftOrder(true);
    store.commit("vuefront/setSidebarCart", true);
  } catch {
    error.value = "We couldn’t prepare your gift order. Please try again.";
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped lang="scss">
.product-gift {
  padding: 20px;
  background: #faf9f7;
  border: 1px solid #e7e4df;
  color: #101820;
  h2 {
    margin: 0 0 8px;
    font-family: var(--vf-font-display);
    font-size: 19px;
    line-height: 1.4;
  }
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.65;
    max-width: 65ch;
  }
  &__links {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 20px;
    margin-top: 10px;
    font-size: 13px;
  }
  a {
    text-decoration: underline;
    text-underline-offset: 3px;
    padding-block: 6px;
  }
  &__choice {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 48px;
    margin-top: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
  input {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    accent-color: #101820;
  }
  &__hint {
    color: #656772;
    font-size: 12px !important;
  }
  &__error {
    margin-top: 10px !important;
    color: #a13d32;
  }
  :is(a, input):focus-visible {
    outline: 2px solid #101820;
    outline-offset: 3px;
  }
}
</style>
